import React, { AnchorHTMLAttributes, ButtonHTMLAttributes } from "react";
import { NavLink, NavLinkProps } from "react-router-dom";
import theme from "../../utils/themeColors";

// Type for common properties across all button types
type CommonButtonProps = {
  variant?:
    | "primary"
    | "secondary"
    | "tertiary"
    | "success"
    | "error"
    | "danger"
    | "ghost"
    | "warning"
    | "info"
    | "link"
    | "outline"
    | "approve"
    | "reject"
    | "orange";
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
  loadingText?: string;
  fullWidth?: boolean;
  isActive?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  to?: string; // For NavLink
  href?: string; // For anchor
  as?: ButtonPropsWithAs;
};

type ButtonPropsWithAs = "button" | "a" | "navlink" | React.ComponentType<any>;

// Type for normal buttons
type ButtonAsButtonProps = CommonButtonProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    as?: "button";
  };

// Type for anchor links
type ButtonAsAnchorProps = CommonButtonProps &
  AnchorHTMLAttributes<HTMLAnchorElement> & {
    as: "a";
    href: string;
  };

// Type for NavLinks
type ButtonAsNavLinkProps = CommonButtonProps & {
  as: "navlink";
  to: string;
};

// Type for custom components
type ButtonAsCustomComponentProps = CommonButtonProps & {
  as: React.ComponentType<any>;
  [key: string]: any;
};

// Union of all possible types
type ButtonProps =
  | ButtonAsButtonProps
  | ButtonAsAnchorProps
  | ButtonAsNavLinkProps
  | ButtonAsCustomComponentProps;

/**
 * Button component
 *
 * A reusable button component with different variants, sizes, and states.
 * Can be rendered as different elements using the 'as' prop.
 *
 * @example
 * // As a button (default)
 * <Button onClick={handleClick}>Click me</Button>
 *
 * // As an anchor
 * <Button as="a" href="/dashboard">Go to Dashboard</Button>
 *
 * // As a NavLink (React Router)
 * <Button as="navlink" to="/profile">Profile</Button>
 */
export const Button = (props: ButtonProps) => {
  const {
    children,
    variant = "primary",
    size = "md",
    isLoading = false,
    loadingText,
    fullWidth = false,
    isActive = false,
    leftIcon,
    rightIcon,
    className = "",
    disabled,
    as = "button",
    to,
    href,
    ...restProps
  } = props;
  // Size classes
  const sizeClasses = {
    sm: "px-2 py-1 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base",
  };

  // Get the button style based on variant
  let buttonStyle = "";

  // Handle different button variants
  switch (variant) {
    case "primary":
      buttonStyle = `${theme.button.primary.base} ${theme.button.primary.dark} ${theme.button.primary.disabled}`;
      break;
    case "secondary":
      buttonStyle = `${theme.button.secondary.base} ${theme.button.secondary.dark} ${theme.button.secondary.disabled}`;
      break;
    case "success":
      buttonStyle = `${theme.button.success.base} ${theme.button.success.dark} ${theme.button.success.disabled}`;
      break;
    case "error":
    case "danger":
      buttonStyle = `${theme.button.danger.base} ${theme.button.danger.dark} ${theme.button.danger.disabled}`;
      break;
    case "warning":
      buttonStyle = `${theme.button.warning.base} ${theme.button.warning.dark} ${theme.button.warning.disabled}`;
      break;
    case "tertiary":
      buttonStyle = `${theme.button.tertiary.base} ${theme.button.tertiary.dark} ${theme.button.tertiary.disabled}`;
      break;
    case "approve":
      buttonStyle = isActive
        ? `${theme.button.approve.active} ${theme.button.approve.disabled}`
        : `${theme.button.approve.base} ${theme.button.approve.dark} ${theme.button.approve.disabled}`;
      break;
    case "reject":
      buttonStyle = isActive
        ? `${theme.button.reject.active} ${theme.button.reject.disabled}`
        : `${theme.button.reject.base} ${theme.button.reject.dark} ${theme.button.reject.disabled}`;
      break;
    case "orange":
      buttonStyle = `${theme.button.orange.base} ${theme.button.orange.dark} ${theme.button.orange.disabled}`;
      break;
    case "outline":
      buttonStyle =
        "border border-gray-300 bg-transparent hover:bg-gray-50 text-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700";
      break;
    case "ghost":
      buttonStyle = "";
      break;
    case "link":
      buttonStyle =
        "text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300";
      break;
    default:
      buttonStyle = `${theme.button.primary.base} ${theme.button.primary.dark} ${theme.button.primary.disabled}`;
  }

  // Common content for all button types
  const content = (
    <>
      {isLoading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}
      {!isLoading && leftIcon && <span className="mr-2">{leftIcon}</span>}
      {isLoading && loadingText ? loadingText : children}
      {!isLoading && rightIcon && <span className="ml-2">{rightIcon}</span>}
    </>
  );

  // Common classes for all button types
  const commonClasses = `
    inline-flex items-center justify-center font-medium rounded-full
    transition-colors duration-150 ease-in-out
    ${variant === "link" ? "" : sizeClasses[size]}
    ${buttonStyle}
    ${fullWidth ? "w-full" : ""}
    ${disabled || isLoading ? "opacity-60 cursor-not-allowed" : ""}
    ${className}
  `;

  // Render based on the type specified in 'as'
  if (as === "a") {
    const anchorProps: AnchorHTMLAttributes<HTMLAnchorElement> = {
      className: commonClasses,
      href,
      ...(restProps as AnchorHTMLAttributes<HTMLAnchorElement>),
    };

    return <a {...anchorProps}>{content}</a>;
  }

  if (as === "navlink" && to) {
    // Filter button-specific properties that are not compatible with NavLink
    const navLinkProps = {
      to,
      className: commonClasses,
    } as NavLinkProps;

    // Add safe properties (exclude button-specific ones)
    const buttonSpecificProps = [
      "type",
      "form",
      "formAction",
      "formEncType",
      "formMethod",
      "formNoValidate",
      "formTarget",
      "name",
      "value",
    ];

    Object.entries(restProps).forEach(([key, value]) => {
      if (!buttonSpecificProps.includes(key)) {
        (navLinkProps as any)[key] = value;
      }
    });

    return <NavLink {...navLinkProps}>{content}</NavLink>;
  }

  if (typeof as === "function") {
    const Component = as;
    return (
      <Component
        className={commonClasses}
        disabled={disabled || isLoading}
        {...restProps}
      >
        {content}
      </Component>
    );
  }

  // By default, render as a button
  const buttonProps: ButtonHTMLAttributes<HTMLButtonElement> = {
    className: commonClasses,
    disabled: disabled || isLoading,
    ...(restProps as ButtonHTMLAttributes<HTMLButtonElement>),
  };

  return <button {...buttonProps}>{content}</button>;
};

/**
 * Refresh Button component
 *
 * A specialized button for refresh actions with loading state.
 * Can be rendered as different elements using the 'as' prop.
 *
 * @example
 * // As a button (default)
 * <RefreshButton onClick={handleRefresh} />
 *
 * // As an anchor
 * <RefreshButton as="a" href="/refresh" />
 *
 * // As a NavLink (React Router)
 * <RefreshButton as="navlink" to="/refresh" />
 */
export interface RefreshButtonProps extends Omit<
  ButtonProps,
  "leftIcon" | "rightIcon"
> {
  defaultText?: string;
  onClick?: () => void;
  as?: ButtonPropsWithAs;
}

export const RefreshButton: React.FC<RefreshButtonProps> = ({
  isLoading = false,
  onClick,
  className = `${theme.button.primary} w-[110px]`,
  loadingText = "Loading",
  defaultText = "Refresh",
  as,
  to,
  href,
  ...props
}) => {
  // Refresh icon SVG
  const RefreshIconSvg = () => (
    <svg
      className="fill-current shrink-0"
      width="16"
      height="16"
      viewBox="0 0 16 16"
    >
      <path d="M4.3 4.5c1.9-1.9 5.1-1.9 7 0 .7.7 1.2 1.7 1.4 2.7l2-.3c-.2-1.5-.9-2.8-1.9-3.8C10.1.4 5.7.4 2.9 3.1L.7.9 0 7.3l6.4-.7-2.1-2.1zM15.6 8.7l-6.4.7 2.1 2.1c-1.9 1.9-5.1 1.9-7 0-.7-.7-1.2-1.7-1.4-2.7l-2 .3c.2 1.5.9 2.8 1.9 3.8 1.4 1.4 3.1 2 4.9 2 1.8 0 3.6-.7 4.9-2l2.2 2.2.8-6.4z" />
    </svg>
  );

  // Use the main Button component with refresh icon
  return (
    <Button
      as={as as React.ComponentType<any>}
      to={to}
      href={href}
      isLoading={isLoading}
      onClick={onClick}
      className={className}
      loadingText={loadingText}
      leftIcon={!isLoading ? <RefreshIconSvg /> : undefined}
      {...props}
    >
      {defaultText}
    </Button>
  );
};
